import {useEffect, useRef, useState} from "react";
import log from "@/lib/logger";
import {useAdminTranslation, useCommonTranslation} from "@/i18n/hooks";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Button} from "@/components/tailwind/button.tsx";
import {Input} from "@/components/tailwind/input.tsx";
import {Text} from "@/components/tailwind/text.tsx";
import {Switch} from "@/components/tailwind/switch.tsx";
import {Badge} from "@/components/tailwind/badge";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {CheckCircle, GripVertical, Pencil, Plus, Trash, XCircle, AlertCircle} from "lucide-react";
import {v4 as uuid} from "uuid";
import {SubmissionChannel} from "@/models/submission-channel.ts";
import {Pagination} from "@/components/pagination.tsx";
import LoadingSpinner from "@/components/loader-spinner.tsx";
import {
    createSubmissionChannel,
    deleteSubmissionChannel,
    fetchAllSubmissionChannel,
    setDefaultSubmissionChannel,
    updateSubmissionChannelDetails,
    updateSubmissionChannelOrder
} from "@/services/submission-channel-services.ts";
import {useAuth} from "@/context/use-auth.tsx";
import ValidationAlert from "@/components/validation-alert.tsx";
import {useToast} from "@/hooks/use-toast.ts";

type SortableItemProps = {
    channel: SubmissionChannel;
    editingId: string | null;
    handleStartEditing: (id: string) => void;
    handleSaveEditing: (id: string, newName: string, isDefault: boolean) => void;
    handleCancelEditing: () => void;
    handleToggleDefault: (id: string) => void;
    handleDelete: (id: string) => void;
    t: any;
    isUpdating: boolean;
};

function SortableItem({
                          channel,
                          editingId,
                          handleStartEditing,
                          handleSaveEditing,
                          handleCancelEditing,
                          handleToggleDefault,
                          handleDelete,
                          t,
                          isUpdating,
                      }: SortableItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition
    } = useSortable({id: channel.id});

    const [isDefault, setIsDefault] = useState(channel.isDefault);
    const [editName, setEditName] = useState(channel.name);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 bg-white dark:bg-zinc-900 shadow-sm flex items-center justify-between"
            data-testid={`submission-channel-item-${channel.id}`}
        >
            {editingId === channel.id ? (
                <div className="flex items-center w-full gap-4">
                    <Input
                        className="flex-grow"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        id={`edit-channel-${channel.id}`}
                        placeholder={t("adminSettings.submissionChannels.namePlaceholder")}
                        data-testid={`edit-channel-name-${channel.id}`}
                    />
                    <div className="flex items-center gap-2">
                        <Text className="text-zinc-700 dark:text-zinc-300">Standard:</Text>
                        <Switch
                            checked={isDefault}
                            onChange={setIsDefault}
                            id={`default-switch-${channel.id}`}
                        />
                    </div>
                    <Button
                        plain
                        onClick={() => handleSaveEditing(channel.id, editName, isDefault)}
                        data-testid={`save-edit-channel-${channel.id}`}
                    >
                        <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400"/>
                    </Button>

                    <Button
                        plain
                        onClick={handleCancelEditing}
                        data-testid={`cancel-edit-channel-${channel.id}`}
                    >
                        <XCircle className="h-5 w-5 text-red-500 dark:text-red-400"/>
                    </Button>
                </div>
            ) : (
                <>
                    <div className="flex items-center">
                        <div
                            className="flex items-center justify-center mr-3 cursor-move text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300"
                            {...attributes}
                            {...listeners}
                        >
                            <GripVertical className="h-5 w-5"/>
                        </div>
                        <span className="mr-2 text-zinc-500 dark:text-zinc-400">#{channel.sortOrder}</span>
                        <Text className="text-zinc-900 dark:text-zinc-100" data-testid={`channel-name-${channel.id}`}>{channel.name}</Text>
                        {channel.isDefault && (
                            <Badge color="amber" className="ml-2" data-testid={`default-badge-${channel.id}`}>
                                {t("adminSettings.submissionChannels.default")}
                            </Badge>
                        )}
                    </div>
                    <div className="flex space-x-2">
                        {!channel.isDefault && (
                            <Button
                                plain
                                onClick={() => handleToggleDefault(channel.id)}
                                title={t("adminSettings.submissionChannels.setAsDefault")}
                                data-testid={`set-default-channel-${channel.id}`}
                            >
                                <CheckCircle
                                    className="h-5 w-5 text-zinc-400 dark:text-zinc-500 hover:text-green-500 dark:hover:text-green-400"/>
                            </Button>
                        )}
                        <Button
                            plain
                            onClick={() => handleStartEditing(channel.id)}
                            title={t("adminSettings.submissionChannels.edit")}
                            disabled={isUpdating}
                            data-testid={`edit-channel-${channel.id}`}
                        >
                            <Pencil
                                className={`h-5 w-5 text-zinc-500 dark:text-zinc-400 ${!isUpdating ? 'hover:text-blue-500 dark:hover:text-blue-400' : ''}`}/>
                        </Button>
                        <Button
                            plain
                            onClick={() => handleDelete(channel.id)}
                            title={t("adminSettings.submissionChannels.delete")}
                            data-testid={`delete-channel-${channel.id}`}
                        >
                            <Trash
                                className="h-5 w-5 text-zinc-500 dark:text-zinc-400 hover:text-red-500 dark:hover:text-red-400"/>
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}

interface SectionSubmissionChannelProps {
    isMissing?: boolean;
}

function SectionSubmissionChannel({ isMissing = false }: SectionSubmissionChannelProps) {
    const {t} = useAdminTranslation();
    const {t: tCommon} = useCommonTranslation();
    const {token} = useAuth()
    const {toast} = useToast()

    const [channels, setChannels] = useState<SubmissionChannel[]>([]);
    const [prevChannels, setPrevChannels] = useState<SubmissionChannel[]>([]);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [hasOrderChanged, setHasOrderChanged] = useState<boolean>(false);

    const [editingId, setEditingId] = useState<string | null>(null);
    const [newChannel, setNewChannel] = useState<string>("");
    const [currentPage, setCurrentPage] = useState(1);
    const [isAdding, setIsAdding] = useState(false);
    const [addingIssue, setAddingIssue] = useState<string | null>(null);
    const [orderUpdateIssue, setOrderUpdateIssue] = useState<string | null>(null);
    const [editChannelIssue, setEditChannelIssue] = useState<string | null>(null);

    const isFirstRender = useRef(true);

    const itemsPerPage = 5;
    const pageCount = Math.ceil(channels.filter(c => !c.isDeleted).length / itemsPerPage);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    function showToast(title: string, description: string, variant: "default" | "destructive" = "default") {
        return (
            toast({
                title: title,
                description: description,
                variant: variant
            })
        )
    }

    async function fetchSubmissionChannels() {
        setIsUpdating(true);
        try {
            const channels = await fetchAllSubmissionChannel(token)

            if (channels) {
                setChannels(
                    channels.sort((a, b) => a.sortOrder - b.sortOrder)
                );
                setPrevChannels([])
            }
        } catch (error) {
            log.error("Error fetching submission channels. Error: ", error);
        }

        setIsUpdating(false)

    }

    useEffect(() => {
        fetchSubmissionChannels()
    }, []);

    useEffect(() => {

        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (isUpdating || !hasOrderChanged)
            return

        const timer = setTimeout(() => {
            updateChannelOrderInBackend(channels)
        }, 300)

        return () => clearTimeout(timer)
    }, [channels, isUpdating, hasOrderChanged])

    useEffect(() => {
        const lastPage = Math.max(1, pageCount);
        if (currentPage > lastPage) {
            setCurrentPage(lastPage);
        }
    }, [pageCount, currentPage])

    const paginatedChannels = channels
        .filter(channel => !channel.isDeleted)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    async function updateChannelOrderInBackend(updatedSubmissionChannels: SubmissionChannel[]) {
        try {
            setIsUpdating(true);

            log.debug("Updating channels on backend: ", updatedSubmissionChannels);

            const orderedIds = updatedSubmissionChannels
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((channel) => channel.id);
            log.debug("Ordered list: ", orderedIds);

            updateSubmissionChannelOrder(token, orderedIds)
                .then((response: SubmissionChannel[] | void) => {
                    if (response) {
                        setChannels(response)
                    }
                })
                .catch((error) => {
                    log.error("Error updating channels for backend: ", error);

                    if (error.validationIssues) {
                        setOrderUpdateIssue(t(error.validationIssues[0].message));
                    } else if (error.status) {
                        setOrderUpdateIssue(tCommon("serverError.responseCodeWithDetails", {
                            statusCode: error.status,
                            errorDetails: error.error
                        }));
                    } else {
                        setOrderUpdateIssue(tCommon("serverError.unknownWithDetails", {errorDetails: error.message}));
                    }

                })
                .finally(() => {
                    setHasOrderChanged(false);
                    setIsUpdating(false)
                });
        } catch (error) {
            log.error("Fehler beim Aktualisieren:", error);

            showToast(
                t("adminSettings.submissionChannels.toast.updateOrderError.title"),
                t("adminSettings.submissionChannels.toast.updateOrderError.description"),
                "destructive"
            )

            setChannels(prevChannels);
            setIsUpdating(false);
        }
    }

    function handleDragEnd(event: DragEndEvent) {
        const {active, over} = event;

        if (over && active.id !== over.id) {
            setPrevChannels(channels)

            setChannels((items: SubmissionChannel[]) => {
                const oldIndex = items.findIndex(item => item.id === active.id);
                const newIndex = items.findIndex(item => item.id === over.id);

                const newItems = arrayMove(items, oldIndex, newIndex);

                setHasOrderChanged(true)

                // new sort order
                return newItems.map((item, index) => ({
                    ...item,
                    sortOrder: index + 1
                }));
            });
        }
    }

    function handleStartEditing(id: string) {
        setEditingId(id);
    }

    function handleCancelEditing() {
        setEditingId(null);
    }

    function handleSaveEditing(id: string, newName: string, isDefault: boolean) {
        setChannels(channels.map(channel =>
            channel.id === id
                ? {...channel, name: newName, isDefault}
                : isDefault ? {...channel, isDefault: false} : channel
        ));

        log.debug("isDefault: ", isDefault);

        try {
            setIsUpdating(true);

            const dto = {
                id: id,
                name: newName,
                isDefault: isDefault
            }
            log.debug("DTO: ", dto)

            updateSubmissionChannelDetails(token, dto)
                .then((response: SubmissionChannel | void) => {
                    log.debug("Success: ", response);

                    showToast(
                        t("adminSettings.submissionChannels.toast.updated.title"),
                        t("adminSettings.submissionChannels.toast.updated.description", {channelName: newName}),
                    )

                    setEditChannelIssue(null);
                    setEditingId(null);
                    setIsUpdating(false);

                    fetchSubmissionChannels()
                })
                .catch((error) => {
                    log.error(`Error updating ${id}: `, error);

                    if (error.validationIssues) {
                        setEditChannelIssue(t(error.validationIssues[0].message));
                    } else if (error.status) {
                        setEditChannelIssue(tCommon("serverError.responseCodeWithDetails", {
                            statusCode: error.status,
                            errorDetails: error.error
                        }));
                    } else {
                        setEditChannelIssue(tCommon("serverError.unknownWithDetails", {errorDetails: error.message}));
                    }
                })


        } catch (error) {
            log.error("Error while updating submission channel: ", error);
            showToast(
                t("adminSettings.submissionChannels.toast.editError.title"),
                t("adminSettings.submissionChannels.toast.editError.description"),
                "destructive"
            )
        } finally {
            setIsUpdating(false);
        }

    }

    function handleDelete(id: string) {
        // preserve current set, in case something goes wrong
        setPrevChannels(channels)

        try {
            setIsUpdating(true);
            const response = deleteSubmissionChannel(token, id);

            response
                .then(() => {
                    log.debug(`Successfully deleted ${id}`);

                    showToast(
                        t("adminSettings.submissionChannels.toast.deleted.title"),
                        t("adminSettings.submissionChannels.toast.deleted.description"),
                    )

                    fetchSubmissionChannels()
                })
                .catch((error) => {
                    log.error("Promise errored: ", error);

                    showToast(
                        t("adminSettings.submissionChannels.toast.deleteError.title"),
                        t("adminSettings.submissionChannels.toast.deleteError.description"),
                        "destructive"
                    )
                    // restore old channel list
                    setChannels(prevChannels);
                }).finally(() => setIsUpdating(false))

        } catch (error) {
            // restore old channel list
            setChannels(prevChannels);
            log.error(`Error deleting entry with id ${id}:`, error);

            showToast(
                t("adminSettings.submissionChannels.toast.deleteError.title"),
                t("adminSettings.submissionChannels.toast.deleteError.description"),
                "destructive"
            )

        }
    }

    function handleAddNewChannel() {
        if (newChannel.trim() === "") return;

        const newEntry: SubmissionChannel = {
            id: uuid(),
            name: newChannel,
            sortOrder: channels.length + 1,
            isDefault: channels.length === 0
        };

        try {
            setIsUpdating(true)

            const response = createSubmissionChannel(token, newEntry);

            response
                .then((newChannel: SubmissionChannel | void) => {
                    if (newChannel) {
                        setChannels([...channels, newChannel]);

                        showToast(
                            t("adminSettings.submissionChannels.toast.created.title"),
                            t("adminSettings.submissionChannels.toast.created.description", {
                                channelName: newChannel.name
                            }),
                        )

                        setNewChannel("");
                        setIsAdding(false);
                        setAddingIssue(null)
                    }
                })
                .catch((error) => {
                    if (error.validationIssues) {
                        setAddingIssue(t(error.validationIssues[0].message));
                    } else if (error.status) {
                        setAddingIssue(tCommon("serverError.responseCodeWithDetails", {
                            statusCode: error.status,
                            errorDetails: error.error
                        }));
                    } else {
                        setAddingIssue(tCommon("serverError.unknownWithDetails", {errorDetails: error.message}));
                    }
                }).finally(() => setIsUpdating(false))
        } catch (error) {
            log.error("Unable to create new submission channel.", error);


            showToast(
                t("adminSettings.submissionChannels.toast.creationError.title"),
                t("adminSettings.submissionChannels.toast.creationError.description", {
                    channelName: newChannel
                }),
                "destructive"
            )

            setIsAdding(false);
        }
    }

    function handleToggleDefault(id: string) {
        try {
            setIsUpdating(true)
            setDefaultSubmissionChannel(token, id)
                .then(() => {
                    setChannels(channels.map(channel => ({
                        ...channel,
                        isDefault: channel.id === id
                    })));
                })
                .catch((error) => {
                    if (error.validationIssues) {
                        setAddingIssue(t(error.validationIssues[0].message));
                    } else if (error.status) {
                        setAddingIssue(tCommon("serverError.responseCodeWithDetails", {
                            statusCode: error.status,
                            errorDetails: error.error
                        }));
                    } else {
                        setAddingIssue(tCommon("serverError.unknownWithDetails", {errorDetails: error.message}));
                    }
                })
                .finally(() => setIsUpdating(false))

        } catch (error) {
            log.error("Error updating default flag", error);
        }

    }

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            {isUpdating && (
                <div
                    className="absolute inset-0 bg-white/60 dark:bg-zinc-950/60 flex items-center justify-center z-10 rounded-lg">
                    <div className="bg-white dark:bg-zinc-900 p-4 rounded-lg shadow-lg flex items-center gap-3">
                        <LoadingSpinner/>
                        <Text>{tCommon("loading")}</Text>
                    </div>
                </div>
            )}

            <CardHeader>
                <div className="flex justify-between items-start gap-4">
                    <div className="max-w-2xl">
                        <CardTitle className="text-xl font-bold flex items-center gap-2">
                            {t("adminSettings.submissionChannels.title")}
                            {isMissing && (
                                <span 
                                    title={t("adminSettings.missingConfiguration.tooltip")}
                                    aria-label={t("adminSettings.missingConfiguration.ariaLabel")}
                                >
                                    <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                                </span>
                            )}
                        </CardTitle>
                        <Text className="mt-1 text-sm text-muted-foreground">{t("adminSettings.submissionChannels.description")}</Text>
                    </div>
                    {!isAdding && (
                        <Button
                            outline
                            onClick={() => setIsAdding(true)}
                            disabled={isUpdating}
                            data-testid="add-submission-channel-button"
                            className="whitespace-nowrap"
                        >
                            <Plus className="h-5 w-5 mr-2" data-slot="icon"/>
                            {t("adminSettings.submissionChannels.addNew")}
                        </Button>
                    )}
                </div>
            </CardHeader>

            {orderUpdateIssue && (
                <ValidationAlert message={orderUpdateIssue || tCommon("serverErrors.unknown")}/>
            )}

            {editChannelIssue && (
                <ValidationAlert message={editChannelIssue || tCommon("serverErrors.unknown")}/>
            )}

            <CardContent>


                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={paginatedChannels.map(channel => channel.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="space-y-3 mb-6">
                            {paginatedChannels.map((channel) => (
                                <SortableItem
                                    key={channel.id}
                                    channel={channel}
                                    editingId={editingId}
                                    handleStartEditing={handleStartEditing}
                                    handleSaveEditing={handleSaveEditing}
                                    handleCancelEditing={handleCancelEditing}
                                    handleToggleDefault={handleToggleDefault}
                                    handleDelete={handleDelete}
                                    t={t}
                                    isUpdating={isUpdating}
                                />
                            ))}
                            {paginatedChannels.length === 0 && (
                                <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded">
                                    <Text className="text-blue-700 dark:text-blue-300">
                                        {t("adminSettings.submissionChannels.noItems")}
                                    </Text>
                                </div>
                            )}
                        </div>
                    </SortableContext>
                </DndContext>

                {isAdding && (
                    <>
                        <div className="flex items-center gap-2 mt-4 mb-2">
                            <Input
                                value={newChannel}
                                onChange={(e) => setNewChannel(e.target.value)}
                                placeholder={t("adminSettings.submissionChannels.newChannelPlaceholder")}
                                className="flex-grow"
                                data-testid="submission-channel-name"
                            />
                            <Button
                                color="green"
                                onClick={handleAddNewChannel}
                                data-testid="submit-submission-channel"
                            >
                                {t("adminSettings.submissionChannels.saveNew")}
                            </Button>
                            <Button
                                outline
                                onClick={() => {
                                    setNewChannel("")
                                    setIsAdding(false)
                                }}
                            >
                                {t("adminSettings.submissionChannels.cancel")}
                            </Button>
                        </div>
                        {addingIssue && (
                            <ValidationAlert message={addingIssue}/>
                        )}
                    </>
                )}

                {pageCount > 1 && (
                    <div className="mt-6 flex justify-center">
                        <Pagination
                            count={pageCount}
                            page={currentPage}
                            onChange={(page) => setCurrentPage(page)}
                        />
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default SectionSubmissionChannel;
